// shopImage.js
// The picture shop.rxpros.com/s shows for a catalog product, so a product bought on
// the shop looks the same everywhere in this app (Ale, 2026-11-03).
//
// The shop's own rule (rxpros-shop src/routes/storefront/model.js):
//   1. six products draw a redrawn vial, keyed by the product's `url`
//      (rxpros-shop src/routes/home/vials.js);
//   2. every other product draws its catalog card art, `shop_details.shop.card_image`,
//      moved from NewSelf's bucket onto ours (rxpros-shop src/lib/rxpros-assets.js).
// The Reset Protocol bundle is the exception: the shop draws its three-bottle photo,
// and each version's card art is a single vial. BUNDLES names each version's photo.
//
// Products come from the shop's list (/api/shop/products) and this app's catalog
// (/api/get-all-products): a product can be sold here without being on the grid.
// NAD+ Nasal 3000mg is one, and shares NAD+ Nasal's `url`, so it gets its picture.
//
// `shopImage(id)` answers null until the lists land, and for a product the shop has
// no picture for. Callers keep their own picture for that case. The lookup is
// reactive, so a page drawn before the lists land redraws with the shop picture.

import Vue from 'vue';
import axios from 'axios';
import { getProducts, loadCachedProducts, fetchProductsFromServer } from '@/utils/productData';

const RXPROS_SHOP = 'https://rxpros.s3.us-east-2.amazonaws.com/public/rxpros-shop/';
const NEWSELF_SHOP = 'comnewself.s3.us-east-2.amazonaws.com/public/newself-shop/';

/** rxpros-shop src/routes/home/vials.js, by catalog `url`. Old and new addresses, as the shop keeps them. */
const VIALS = {
    tirzepatide: 'assets/home/vial-tirzepatide.webp',
    semaglutide: 'assets/home/vial-semaglutide.webp',
    nano_nad_Nasal_750: 'assets/home/nasal-nad.webp',
    'nad/nasal': 'assets/home/nasal-nad.webp',
    nano_nad_1500: 'assets/home/vial-nad.webp',
    nad: 'assets/home/vial-nad.webp',
    sermorelin: 'assets/home/vial-sermorelin.webp',
    glutathione: 'assets/home/vial-glutathione.webp',
};

/** The Reset Protocol's three-bottle photos (rxpros-shop src/assets/longevity), by `internal_id`,
 *  because the bundle's `url` differs between dev and prod. Each photo shows that version's bottles. */
const BUNDLES = {
    LONGEVITY_TIRZ: 'assets/longevity/longevity-plan-trio.webp',
    LONGEVITY_TIRZ_NASAL: 'assets/longevity/longevity-plan-trio-tirz-nasal.webp',
    LONGEVITY_SEMA: 'assets/longevity/longevity-plan-trio-sema.webp',
    LONGEVITY_SEMA_NASAL: 'assets/longevity/longevity-plan-trio-sema-nasal.webp',
};

/** Whether a picture link is one of the shop's. */
const isShopPicture = (src) => typeof src === 'string' && src.startsWith(RXPROS_SHOP);

/**
 * How a shop picture sits in its frame, for the class that sizes it: 'vial' for a
 * redrawn vial (framed, the bottle fills 63% of its height, centred), 'spray' for the
 * redrawn NAD+ Nasal (78% of the same frame, its base on the vials' line, so it sits
 * high), 'cutout' for card art (cut to the bottle), '' for a picture that is not the
 * shop's or is a bundle's square photo, which fills the tile as it is.
 */
export const shopPictureFit = (src) => {
    if (!isShopPicture(src) || src.includes('/assets/longevity/')) return '';
    if (src.endsWith('/assets/home/nasal-nad.webp')) return 'spray';
    return src.includes('/assets/home/') ? 'vial' : 'cutout';
};

const rxprosUrl = (url) => {
    if (typeof url !== 'string' || !url) return null;
    const i = url.indexOf(NEWSELF_SHOP);
    return i === -1 ? url : RXPROS_SHOP + url.slice(i + NEWSELF_SHOP.length);
};

function pictureOf(product) {
    if (BUNDLES[product.internal_id]) return RXPROS_SHOP + BUNDLES[product.internal_id];
    if (VIALS[product.url]) return RXPROS_SHOP + VIALS[product.url];
    const shop = product.shop_details && product.shop_details.shop;
    return rxprosUrl(shop && shop.card_image);
}

const state = Vue.observable({ byId: {} });

function add(products) {
    if (!Array.isArray(products)) return;
    const byId = { ...state.byId };
    for (const product of products) {
        const id = product && (product.id || product._id);
        const picture = id && !byId[id] && pictureOf(product);
        if (picture) byId[id] = picture;
    }
    state.byId = byId;
}

function appCatalog() {
    if (!getProducts()) loadCachedProducts();
    return getProducts() ? Promise.resolve(getProducts()) : fetchProductsFromServer().catch(() => null);
}

let loading = null;

/** Loads both lists once. Never rejects: a list that fails leaves its products on their old pictures. */
export function loadShopImages() {
    if (!loading) {
        const shopList = axios.get(`${process.env.VUE_APP_API_ENDPOINT}/api/shop/products`)
            .then((response) => response && response.data && response.data.products)
            .catch((error) => {
                console.error('[shop-image] /api/shop/products failed:', error);
                return null;
            });
        loading = Promise.all([shopList, appCatalog()]).then(([shop, app]) => {
            add(shop);
            add(app);
        });
    }
    return loading;
}

/** The shop's picture for a catalog product id, or null. */
export function shopImage(productId) {
    loadShopImages();
    return (productId && state.byId[productId]) || null;
}
